"use client";

// Paints the signed-in member's OWN saved avatar into the design-hosted header button and account popup (see lib/design-account-face.ts). It reads the same verified
// own-avatar projection every other Root face reads (`useMemberFace`: same-origin /api/me/avatar, per-identity, refreshed on focus), and only sets two custom
// properties + a mode attribute on <html>. Signed out (or on unmount) they are removed, so the guest icon is the design's own.
import { useEffect, useState } from "react";

import { DESIGN_FACE_CSS, FACE_ATTR, FACE_INITIALS_VAR, FACE_PICTURE_VAR, designFace, paintableSrc } from "@/lib/design-account-face";
import { useMemberFace } from "@/components/site/MemberAvatar";

export default function DesignAccountFace() {
  const face = useMemberFace();
  const src = face?.src ?? null;
  const letters = face?.initials ?? null;
  // The chosen address is probed once, as every other face does: an expired or unloadable upload falls back to initials instead of a blank circle. The failure belongs to
  // that address, so a later saved picture (a new address) is tried afresh.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  useEffect(() => {
    const painted = paintableSrc(src);
    if (!painted) return;
    const probe = new Image();
    probe.onerror = () => setFailedSrc(painted);
    probe.referrerPolicy = "no-referrer";
    probe.src = painted;
    return () => {
      probe.onerror = null;
    };
  }, [src]);
  const usable = src !== null && failedSrc !== src ? src : null;

  useEffect(() => {
    const root = document.documentElement;
    if (letters === null) return;
    const f = designFace({ src: usable, initials: letters });
    root.setAttribute(FACE_ATTR, f.mode);
    root.style.setProperty(FACE_INITIALS_VAR, f.initials);
    if (f.picture) root.style.setProperty(FACE_PICTURE_VAR, f.picture);
    else root.style.removeProperty(FACE_PICTURE_VAR);
    return () => {
      root.removeAttribute(FACE_ATTR);
      root.style.removeProperty(FACE_INITIALS_VAR);
      root.style.removeProperty(FACE_PICTURE_VAR);
    };
  }, [usable, letters]);

  return <style data-sg-own-face-css="">{DESIGN_FACE_CSS}</style>;
}
